// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

/**
 * Samba launcher contract v3 (FEAT-241).
 *
 * L0, the trust envelope, is frozen forever: the Authenticode-signed cabinet, the
 * vendor trust anchors and the payload module envelope. L1, the release
 * statement, evolves only through `extensions`. L2, the launcher context, is
 * versioned by interface major and minor.
 */

import { z } from 'zod';
import { RpaModuleNameSchema, type RpaModuleName } from './backend_config.types';
import type { CompileTimeMetadata } from './compile-time/compile-time-metadata';
import type { RpaModuleAuthorizationTarget } from './rpa-module';
import { RpaModuleAuthorizationTargetSchema } from './samba-workflow-release';

/** The payload's workflow release floors, which the launcher checks at startup (NATIVE-STARTUP-001). */
export { SambaWorkflowReleaseSequenceStateV1Schema } from './samba-workflow-release';
import type {
    SambaRuntimePayloadIdentityV1,
    SambaRuntimeProfileActivation,
    SambaRuntimeRelaunchCommand,
    SambaRuntimeRelaunchLifecycle,
    SambaRuntimeStatusV1,
    SambaRuntimeValidationMode,
} from './samba-runtime-payload';

// ---------------------------------------------------------------------------
// Primitives. Enumerations in signed data are open strings: an unknown value
// never matches, and never fails to parse.

export const SambaContractNameSchema: z.ZodType<string> = z
    .string()
    .regex(/^[a-z][a-z0-9-]{0,31}$/);
const MaximumDecimalU63: bigint = 2n ** 63n - 1n;
export const SambaDecimalU63Schema: z.ZodType<string> = z
    .string()
    .regex(/^(?:0|[1-9]\d{0,18})$/)
    .refine((value) => BigInt(value) <= MaximumDecimalU63, 'Value exceeds 2^63-1');
export const SambaSha256HexSchema: z.ZodType<string> = z.string().regex(/^[a-f0-9]{64}$/);
const GitCommitSchema: z.ZodType<string> = z.string().regex(/^[a-f0-9]{40}$/);
const IdentifierSchema: z.ZodType<string> = z.string().regex(/^[a-z0-9][a-z0-9._-]{0,127}$/);
const UtcTimestampSchema: z.ZodType<string> = z.string().datetime();
const InterfaceNumberSchema: z.ZodType<number> = z.number().int().nonnegative().safe();
const ExtensionNameSchema: z.ZodType<string> = z
    .string()
    .regex(/^[a-z][a-z0-9-]*(?:\.[a-z][a-z0-9-]*)+$/);
const ArchiveRelativePathSchema: z.ZodType<string> = z
    .string()
    .regex(/^[A-Za-z0-9._-]+(?:\/[A-Za-z0-9._-]+)*$/)
    .refine(
        (value) => !value.split('/').some((segment) => segment === '.' || segment === '..'),
        'Archive path contains an unsafe segment'
    );

function uniqueArraySchema<Item>(itemSchema: z.ZodType<Item>): z.ZodType<Item[]> {
    return z
        .array(itemSchema)
        .min(1)
        .superRefine((values, context) => {
            if (new Set(values).size !== values.length) {
                context.addIssue({ code: 'custom', message: 'Values must be unique' });
            }
        });
}

// ---------------------------------------------------------------------------
// L2 interface version.

export interface SambaLauncherInterfaceVersion {
    readonly major: number;
    readonly minor: number;
}

export interface SambaLauncherInterfaceRange {
    readonly major: number;
    readonly minimumMinor: number;
}

export const SambaLauncherInterfaceRangeSchema: z.ZodType<SambaLauncherInterfaceRange> = z
    .object({ major: InterfaceNumberSchema, minimumMinor: InterfaceNumberSchema })
    .strict();

const LauncherInterfaceRangesSchema: z.ZodType<SambaLauncherInterfaceRange[]> = z
    .array(SambaLauncherInterfaceRangeSchema)
    .min(1)
    .superRefine((ranges, context) => {
        if (new Set(ranges.map((range) => range.major)).size !== ranges.length) {
            context.addIssue({ code: 'custom', message: 'Interface majors must be unique' });
        }
    });

/** The interface this source tree implements. Bumped only with the registry. */
export const CurrentSambaLauncherInterfaceVersion: SambaLauncherInterfaceVersion = {
    major: 1,
    minor: 0,
};

/** The launcher interfaces this source tree's payload implements; release statements copy it. */
export const CurrentSambaPayloadLauncherInterfaces: readonly SambaLauncherInterfaceRange[] = [
    { major: 1, minimumMinor: 0 },
];

export function supportsSambaLauncherInterface(
    ranges: readonly SambaLauncherInterfaceRange[],
    launcherInterface: SambaLauncherInterfaceVersion
): boolean {
    return ranges.some(
        (range) =>
            range.major === launcherInterface.major && range.minimumMinor <= launcherInterface.minor
    );
}

// ---------------------------------------------------------------------------
// L1 release statement, carried as `release.json` inside each container.

export interface SambaReleaseExtensionV1 {
    readonly critical: boolean;
    readonly scope: 'start' | 'all';
    readonly value: unknown;
}

const ReleaseExtensionsSchema: z.ZodType<Record<string, SambaReleaseExtensionV1>> = z.record(
    ExtensionNameSchema,
    z
        .object({
            critical: z.boolean(),
            scope: z.enum(['start', 'all']),
            value: z.unknown(),
        })
        .strict()
        .transform(
            (extension): SambaReleaseExtensionV1 => ({
                critical: extension.critical,
                scope: extension.scope,
                value: extension.value,
            })
        )
);

export interface SambaRuntimeReleaseStatementV3 {
    readonly schemaVersion: 3;
    readonly statementType: 'runtime-release';
    readonly releaseSequence: string;
    readonly releaseId: string;
    readonly sourceCommit: string;
    readonly issuedAt: string;
    readonly artifact: {
        readonly sha256: string;
        readonly byteLength: string;
        readonly format: string;
        readonly entrypoint: string;
    };
    readonly platforms: readonly string[];
    readonly architectures: readonly string[];
    readonly electronMajors: readonly number[];
    readonly launcherInterfaces: readonly SambaLauncherInterfaceRange[];
    readonly policy: {
        readonly minimumPermittedReleaseSequence: string;
        readonly mandatoryUpdateAfter?: string;
    };
    readonly extensions?: Readonly<Record<string, SambaReleaseExtensionV1>>;
}

export const SambaRuntimeReleaseStatementV3Schema: z.ZodType<SambaRuntimeReleaseStatementV3> = z
    .object({
        schemaVersion: z.literal(3),
        statementType: z.literal('runtime-release'),
        releaseSequence: SambaDecimalU63Schema,
        releaseId: IdentifierSchema,
        sourceCommit: GitCommitSchema,
        issuedAt: UtcTimestampSchema,
        artifact: z
            .object({
                sha256: SambaSha256HexSchema,
                byteLength: SambaDecimalU63Schema,
                format: SambaContractNameSchema,
                entrypoint: ArchiveRelativePathSchema,
            })
            .strict(),
        platforms: uniqueArraySchema(SambaContractNameSchema),
        architectures: uniqueArraySchema(SambaContractNameSchema),
        electronMajors: uniqueArraySchema(z.number().int().positive().safe()),
        launcherInterfaces: LauncherInterfaceRangesSchema,
        policy: z
            .object({
                minimumPermittedReleaseSequence: SambaDecimalU63Schema,
                mandatoryUpdateAfter: UtcTimestampSchema.optional(),
            })
            .strict(),
        extensions: ReleaseExtensionsSchema.optional(),
    })
    .strict()
    .superRefine((statement, context) => {
        if (
            statement.policy.mandatoryUpdateAfter !== undefined &&
            Date.parse(statement.policy.mandatoryUpdateAfter) <= Date.parse(statement.issuedAt)
        ) {
            context.addIssue({
                code: 'custom',
                path: ['policy', 'mandatoryUpdateAfter'],
                message: 'The mandatory-update deadline must follow the issue time.',
            });
        }
    });

export const SambaRuntimeReleaseStatementV3Contract = {
    variants: { v3: { normalize: SambaRuntimeReleaseStatementV3Schema.parse } },
    writerVariant: 'v3',
} as const;

/** Unknown critical extensions in `scopes` make a release unusable for that path. */
export function unknownCriticalSambaReleaseExtensions(
    extensions: Readonly<Record<string, SambaReleaseExtensionV1>> | undefined,
    knownExtensionNames: ReadonlySet<string>,
    scopes: ReadonlySet<SambaReleaseExtensionV1['scope']>
): readonly string[] {
    if (extensions === undefined) {
        return [];
    }
    return Object.keys(extensions).filter((name) => {
        const extension: SambaReleaseExtensionV1 | undefined = extensions[name];
        return (
            extension !== undefined &&
            extension.critical &&
            scopes.has(extension.scope) &&
            !knownExtensionNames.has(name)
        );
    });
}

export interface SambaRuntimeRevocationListV1 {
    readonly schemaVersion: 1;
    readonly statementType: 'revocation-list';
    readonly sequence: string;
    readonly issuedAt: string;
    readonly minimumReleaseSequence: string;
    readonly revokedArtifactSha256: readonly string[];
}

export const SambaRuntimeRevocationListV1Schema: z.ZodType<SambaRuntimeRevocationListV1> = z
    .object({
        schemaVersion: z.literal(1),
        statementType: z.literal('revocation-list'),
        sequence: SambaDecimalU63Schema,
        issuedAt: UtcTimestampSchema,
        minimumReleaseSequence: SambaDecimalU63Schema,
        revokedArtifactSha256: z.array(SambaSha256HexSchema),
    })
    .strict();

export const SambaRuntimeRevocationListContract = {
    variants: { v1: { normalize: SambaRuntimeRevocationListV1Schema.parse } },
    writerVariant: 'v1',
} as const;

export interface SambaWorkflowReleaseStatementV2 {
    readonly schemaVersion: 2;
    readonly statementType: 'workflow-release';
    readonly moduleName: RpaModuleName;
    readonly authorizationTarget: RpaModuleAuthorizationTarget;
    readonly releaseSequence: string;
    readonly releaseId: string;
    readonly sourceCommit: string;
    readonly issuedAt: string;
    readonly artifact: {
        readonly sha256: string;
        readonly byteLength: string;
        readonly format: string;
        readonly entrypoint: string;
    };
    readonly workflowProtocolVersions: readonly number[];
    readonly extensions?: Readonly<Record<string, SambaReleaseExtensionV1>>;
}

export const SambaWorkflowReleaseStatementV2Schema: z.ZodType<SambaWorkflowReleaseStatementV2> = z
    .object({
        schemaVersion: z.literal(2),
        statementType: z.literal('workflow-release'),
        moduleName: RpaModuleNameSchema,
        authorizationTarget: RpaModuleAuthorizationTargetSchema,
        releaseSequence: SambaDecimalU63Schema,
        releaseId: IdentifierSchema,
        sourceCommit: GitCommitSchema,
        issuedAt: UtcTimestampSchema,
        artifact: z
            .object({
                sha256: SambaSha256HexSchema,
                byteLength: SambaDecimalU63Schema,
                format: SambaContractNameSchema,
                entrypoint: ArchiveRelativePathSchema,
            })
            .strict(),
        workflowProtocolVersions: uniqueArraySchema(z.number().int().positive().safe()),
        extensions: ReleaseExtensionsSchema.optional(),
    })
    .strict();

export const SambaWorkflowReleaseStatementV2Contract = {
    variants: { v2: { normalize: SambaWorkflowReleaseStatementV2Schema.parse } },
    writerVariant: 'v2',
} as const;

// ---------------------------------------------------------------------------
// L0 containers and vendor trust anchors.

/** File names inside every container. Frozen. */
export const SambaReleaseContainerFiles = {
    statement: 'release.json',
    payload: 'payload.asar',
    workflow: 'workflow.js',
    revocationList: 'revocation-list.json',
} as const;

/** A hard ceiling compiled into every launcher, independent of any policy. */
export const SambaMaximumContainerBytes: number = 1024 * 1024 * 1024;

/**
 * Every container, on every platform, is one uncompressed cabinet carrying
 * one Azure Artifact Signing Authenticode signature (NATIVE-RUNTIME-001).
 * Windows verifies it with WinVerifyTrust; every other platform verifies the
 * same signature with the launcher's own Authenticode verifier.
 *
 * V1 also named a macOS Developer ID requirement. It was never released and
 * is retired; V2 trusts only the Authenticode identity.
 */
export interface SambaVendorTrustAnchorsV2 {
    readonly schemaVersion: 2;
    /** The Authenticode identity. The key keeps its historical name. */
    readonly win32: {
        /** Roots the code signing chain must end at (SHA-256, upper-case hex). */
        readonly rootSha256Thumbprints: readonly string[];
        /** Roots the RFC 3161 timestamp chain must end at. */
        readonly timestampRootSha256Thumbprints: readonly string[];
        /**
         * Base64 DER of the pinned roots, shipped with the launcher for
         * signatures that omit them. Each must hash to a pinned thumbprint.
         */
        readonly rootCertificatesDer: readonly string[];
        readonly subject: {
            readonly O: string;
            readonly L: string;
            readonly ST: string;
            readonly C: string;
        };
    };
    /** Present only in local and test builds; release builds reject it. */
    readonly local?: {
        readonly win32RootSha256Thumbprint: string;
    };
}

const SubjectFieldSchema: z.ZodType<string> = z.string().min(1).max(128);
const Sha256ThumbprintSchema: z.ZodType<string> = z.string().regex(/^[A-F0-9]{64}$/);

export const SambaVendorTrustAnchorsV2Schema: z.ZodType<SambaVendorTrustAnchorsV2> = z
    .object({
        schemaVersion: z.literal(2),
        win32: z
            .object({
                rootSha256Thumbprints: uniqueArraySchema(Sha256ThumbprintSchema),
                timestampRootSha256Thumbprints: uniqueArraySchema(Sha256ThumbprintSchema),
                rootCertificatesDer: uniqueArraySchema(
                    z
                        .string()
                        .max(16384)
                        .regex(/^(?:[A-Za-z0-9+/]{4})+(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/)
                ),
                subject: z
                    .object({
                        O: SubjectFieldSchema,
                        L: SubjectFieldSchema,
                        ST: SubjectFieldSchema,
                        C: z.string().regex(/^[A-Z]{2}$/),
                    })
                    .strict(),
            })
            .strict(),
        local: z
            .object({
                win32RootSha256Thumbprint: Sha256ThumbprintSchema,
            })
            .strict()
            .optional(),
    })
    .strict();

export const SambaVendorTrustAnchorsContract = {
    variants: { v2: { normalize: SambaVendorTrustAnchorsV2Schema.parse } },
    writerVariant: 'v2',
} as const;

// ---------------------------------------------------------------------------
// Feed (unsigned transport pointers; everything that matters is signed).

export interface SambaRuntimeReleaseQueryV3 {
    readonly schemaVersion: 3;
    readonly platform: string;
    readonly architecture: string;
    readonly electronMajorVersion: number;
    readonly interfaceMajor: number;
    readonly interfaceMinor: number;
    readonly launcherBuildId: string;
}

export const SambaRuntimeReleaseQueryV3Schema: z.ZodType<SambaRuntimeReleaseQueryV3> = z
    .object({
        schemaVersion: z.literal(3),
        platform: SambaContractNameSchema,
        architecture: SambaContractNameSchema,
        electronMajorVersion: z.number().int().positive().safe(),
        interfaceMajor: InterfaceNumberSchema,
        interfaceMinor: InterfaceNumberSchema,
        launcherBuildId: IdentifierSchema,
    })
    .strict();

export const SambaRuntimeReleaseQueryV3Contract = {
    variants: { v3: { normalize: SambaRuntimeReleaseQueryV3Schema.parse } },
    writerVariant: 'v3',
} as const;

/** The reinstall of last resort asks without any interface parameters. */
export interface SambaRuntimeRescueQueryV1 {
    readonly schemaVersion: 1;
    readonly platform: string;
    readonly architecture: string;
    readonly electronMajorVersion: number;
}

export const SambaRuntimeRescueQueryV1Schema: z.ZodType<SambaRuntimeRescueQueryV1> = z
    .object({
        schemaVersion: z.literal(1),
        platform: SambaContractNameSchema,
        architecture: SambaContractNameSchema,
        electronMajorVersion: z.number().int().positive().safe(),
    })
    .strict();

export const SambaRuntimeRescueQueryContract = {
    variants: { v1: { normalize: SambaRuntimeRescueQueryV1Schema.parse } },
    writerVariant: 'v1',
} as const;

export interface SambaContainerPointerV1 {
    readonly containerSha256: string;
    readonly containerByteLength: string;
}

export const SambaContainerPointerV1Schema: z.ZodType<SambaContainerPointerV1> = z
    .object({
        containerSha256: SambaSha256HexSchema,
        containerByteLength: SambaDecimalU63Schema,
    })
    .strict();

export interface SambaRuntimeFeedResponseV3 {
    readonly schemaVersion: 3;
    readonly release: SambaContainerPointerV1 & { readonly releaseSequence: string };
    readonly revocationList?: SambaContainerPointerV1 & { readonly sequence: string };
}

/** Transport container: strict on what it names, but ignores unknown fields. */
export const SambaRuntimeFeedResponseV3Schema: z.ZodType<SambaRuntimeFeedResponseV3> = z
    .object({
        schemaVersion: z.literal(3),
        release: z.object({
            containerSha256: SambaSha256HexSchema,
            containerByteLength: SambaDecimalU63Schema,
            releaseSequence: SambaDecimalU63Schema,
        }),
        revocationList: z
            .object({
                containerSha256: SambaSha256HexSchema,
                containerByteLength: SambaDecimalU63Schema,
                sequence: SambaDecimalU63Schema,
            })
            .optional(),
    })
    .transform(
        (response): SambaRuntimeFeedResponseV3 => ({
            schemaVersion: response.schemaVersion,
            release: {
                containerSha256: response.release.containerSha256,
                containerByteLength: response.release.containerByteLength,
                releaseSequence: response.release.releaseSequence,
            },
            ...(response.revocationList === undefined
                ? {}
                : {
                      revocationList: {
                          containerSha256: response.revocationList.containerSha256,
                          containerByteLength: response.revocationList.containerByteLength,
                          sequence: response.revocationList.sequence,
                      },
                  }),
        })
    );

export const SambaRuntimeFeedResponseContract = {
    variants: { v3: { normalize: SambaRuntimeFeedResponseV3Schema.parse } },
    writerVariant: 'v3',
} as const;

export const SambaRuntimeFeedPaths = {
    latest: '/api/samba/runtime/v3/latest',
    newest: '/api/samba/runtime/v3/newest',
    containers: '/api/samba/runtime/v3/containers',
} as const;

export function sambaRuntimeContainerPath(containerSha256: string): string {
    return `${SambaRuntimeFeedPaths.containers}/${SambaSha256HexSchema.parse(containerSha256)}`;
}

// ---------------------------------------------------------------------------
// Server-side catalog of the containers a PILLAR image serves. Unsigned: it is
// PILLAR's own index, derived from the signed statements by the release build.

export interface SambaPlatformContainerV1 {
    readonly platform: string;
    readonly containerSha256: string;
    readonly containerByteLength: string;
}

const PlatformContainersSchema: z.ZodType<SambaPlatformContainerV1[]> = z
    .array(
        z
            .object({
                platform: SambaContractNameSchema,
                containerSha256: SambaSha256HexSchema,
                containerByteLength: SambaDecimalU63Schema,
            })
            .strict()
    )
    .min(1)
    .superRefine((containers, context) => {
        if (new Set(containers.map((container) => container.platform)).size !== containers.length) {
            context.addIssue({ code: 'custom', message: 'One container per platform' });
        }
    });

export interface SambaRuntimeCatalogReleaseV3 {
    readonly releaseSequence: string;
    readonly releaseId: string;
    readonly sourceCommit: string;
    readonly payloadSha256: string;
    readonly launcherInterfaces: readonly SambaLauncherInterfaceRange[];
    readonly platforms: readonly string[];
    readonly architectures: readonly string[];
    readonly electronMajors: readonly number[];
    readonly minimumPermittedReleaseSequence: string;
    readonly containers: readonly SambaPlatformContainerV1[];
}

export interface SambaWorkflowCatalogReleaseV3 {
    readonly moduleName: RpaModuleName;
    readonly releaseSequence: string;
    readonly workflowProtocolVersions: readonly number[];
    readonly containers: readonly SambaPlatformContainerV1[];
}

export interface SambaRuntimeCatalogV3 {
    readonly schemaVersion: 3;
    readonly releases: readonly SambaRuntimeCatalogReleaseV3[];
    readonly workflows: readonly SambaWorkflowCatalogReleaseV3[];
    readonly revocationList?: {
        readonly sequence: string;
        readonly containers: readonly SambaPlatformContainerV1[];
    };
}

export const SambaRuntimeCatalogFileName = 'catalog-v3.json';
export const SambaRuntimeCatalogContainersDirectory = 'containers';

export const SambaRuntimeCatalogV3Schema: z.ZodType<SambaRuntimeCatalogV3> = z
    .object({
        schemaVersion: z.literal(3),
        releases: z.array(
            z
                .object({
                    releaseSequence: SambaDecimalU63Schema,
                    releaseId: IdentifierSchema,
                    sourceCommit: GitCommitSchema,
                    payloadSha256: SambaSha256HexSchema,
                    launcherInterfaces: LauncherInterfaceRangesSchema,
                    platforms: uniqueArraySchema(SambaContractNameSchema),
                    architectures: uniqueArraySchema(SambaContractNameSchema),
                    electronMajors: uniqueArraySchema(z.number().int().positive().safe()),
                    minimumPermittedReleaseSequence: SambaDecimalU63Schema,
                    containers: PlatformContainersSchema,
                })
                .strict()
        ),
        workflows: z.array(
            z
                .object({
                    moduleName: RpaModuleNameSchema,
                    releaseSequence: SambaDecimalU63Schema,
                    workflowProtocolVersions: uniqueArraySchema(z.number().int().positive().safe()),
                    containers: PlatformContainersSchema,
                })
                .strict()
        ),
        revocationList: z
            .object({ sequence: SambaDecimalU63Schema, containers: PlatformContainersSchema })
            .strict()
            .optional(),
    })
    .strict()
    .superRefine((catalog, context) => {
        if (
            new Set(catalog.releases.map((release) => release.releaseSequence)).size !==
            catalog.releases.length
        ) {
            context.addIssue({ code: 'custom', message: 'Release sequences must be unique' });
        }
        const workflowKeys = catalog.workflows.map(
            (workflow) => `${workflow.moduleName}@${workflow.releaseSequence}`
        );
        if (new Set(workflowKeys).size !== workflowKeys.length) {
            context.addIssue({ code: 'custom', message: 'Workflow releases must be unique' });
        }
    });

export const SambaRuntimeCatalogContract = {
    variants: { v3: { normalize: SambaRuntimeCatalogV3Schema.parse } },
    writerVariant: 'v3',
} as const;

// ---------------------------------------------------------------------------
// Launcher-owned files under userData.

export const SambaRuntimeInstallationBindingFileName = 'samba-runtime-installation-v1.json';
/**
 * v2 holds only a portal the user chose. The v1 file is no longer read:
 * builds that wrote it also saved an older build's default portal as if
 * chosen, so it cannot tell a choice from a stale default (Alex, 2026-10-29).
 */
export const SambaPortalSelectionFileName = 'samba-portal-v2.json';

export interface SambaRuntimeInstallationBindingV1 {
    readonly schemaVersion: 1;
    readonly launcherBuildId: string;
    readonly interface: SambaLauncherInterfaceVersion;
    readonly installationFingerprint: string;
    readonly highestRevocationSequence: string;
    readonly boundAt: string;
    readonly pendingAdoption?: {
        readonly token: string;
        readonly createdAt: string;
    };
}

export const SambaRuntimeInstallationBindingV1Schema: z.ZodType<SambaRuntimeInstallationBindingV1> =
    z
        .object({
            schemaVersion: z.literal(1),
            launcherBuildId: IdentifierSchema,
            interface: z
                .object({ major: InterfaceNumberSchema, minor: InterfaceNumberSchema })
                .strict(),
            installationFingerprint: SambaSha256HexSchema,
            highestRevocationSequence: SambaDecimalU63Schema,
            boundAt: UtcTimestampSchema,
            pendingAdoption: z
                .object({
                    token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
                    createdAt: UtcTimestampSchema,
                })
                .strict()
                .optional(),
        })
        .strict();

export const SambaRuntimeInstallationBindingStorageContract = {
    variants: { v1: { normalize: SambaRuntimeInstallationBindingV1Schema.parse } },
    writerVariant: 'v1',
} as const;

export interface SambaPortalSelectionV1 {
    readonly schemaVersion: 1;
    readonly portalOrigin: string;
}

export const SambaPortalSelectionV1Schema: z.ZodType<SambaPortalSelectionV1> = z
    .object({
        schemaVersion: z.literal(1),
        portalOrigin: z
            .string()
            .url()
            .refine((value) => new URL(value).origin === value, 'Must be an origin'),
    })
    .strict();

export interface SambaPortalSelectionV2 {
    readonly schemaVersion: 2;
    readonly portalOrigin: string;
}

export const SambaPortalSelectionV2Schema: z.ZodType<SambaPortalSelectionV2> = z
    .object({
        schemaVersion: z.literal(2),
        portalOrigin: z
            .string()
            .url()
            .refine((value) => new URL(value).origin === value, 'Must be an origin'),
    })
    .strict();

export const SambaPortalSelectionStorageContract = {
    variants: { v2: { normalize: SambaPortalSelectionV2Schema.parse } },
    writerVariant: 'v2',
} as const;

/** One cached release, keyed by its container digest. */
export interface SambaRuntimeCachedReleaseV1 {
    readonly containerSha256: string;
    readonly payload: SambaRuntimePayloadIdentityV1;
}

export interface SambaRuntimeOriginStateV1 {
    readonly highestAcceptedReleaseSequence: string;
    readonly feedValidator?: {
        readonly etag: string;
        readonly containerSha256: string;
    };
    readonly knownGood?: SambaRuntimeCachedReleaseV1;
    readonly previousKnownGood?: SambaRuntimeCachedReleaseV1;
    readonly candidate?: SambaRuntimeCachedReleaseV1 & {
        readonly attempt?: { readonly attemptId: string; readonly startedAt: string };
    };
    readonly lastVerifiedPolicy?: {
        readonly releaseSequence: string;
        readonly minimumPermittedReleaseSequence: string;
        readonly verifiedAt: string;
        readonly mandatoryUpdateAfter?: string;
    };
}

export interface SambaRuntimeLauncherStateV3 {
    readonly schemaVersion: 3;
    readonly origins: Readonly<Record<string, SambaRuntimeOriginStateV1>>;
    readonly revocationList?: {
        readonly sequence: string;
        readonly minimumReleaseSequence: string;
        readonly revokedArtifactSha256: readonly string[];
    };
    readonly lastHttpValidation?: {
        readonly checkedAt: string;
        readonly result: 'not-modified' | 'current' | 'candidate';
        readonly releaseSequence: string;
    };
    readonly lastFailure?: {
        readonly occurredAt: string;
        readonly phase: 'startup' | 'update-check';
        readonly reason: 'runtime-startup-failed' | 'runtime-update-check-failed';
    };
}

const PayloadIdentitySchema: z.ZodType<SambaRuntimePayloadIdentityV1> = z
    .object({
        schemaVersion: z.literal(1),
        releaseSequence: SambaDecimalU63Schema,
        releaseId: IdentifierSchema,
        sourceCommit: GitCommitSchema,
        sha256: SambaSha256HexSchema,
    })
    .strict();

const CachedReleaseSchema: z.ZodType<SambaRuntimeCachedReleaseV1> = z
    .object({ containerSha256: SambaSha256HexSchema, payload: PayloadIdentitySchema })
    .strict();

const OriginStateSchema: z.ZodType<SambaRuntimeOriginStateV1> = z
    .object({
        highestAcceptedReleaseSequence: SambaDecimalU63Schema,
        feedValidator: z
            .object({ etag: z.string().min(1), containerSha256: SambaSha256HexSchema })
            .strict()
            .optional(),
        knownGood: CachedReleaseSchema.optional(),
        previousKnownGood: CachedReleaseSchema.optional(),
        candidate: z
            .object({
                containerSha256: SambaSha256HexSchema,
                payload: PayloadIdentitySchema,
                attempt: z
                    .object({ attemptId: IdentifierSchema, startedAt: UtcTimestampSchema })
                    .strict()
                    .optional(),
            })
            .strict()
            .optional(),
        lastVerifiedPolicy: z
            .object({
                releaseSequence: SambaDecimalU63Schema,
                minimumPermittedReleaseSequence: SambaDecimalU63Schema,
                verifiedAt: UtcTimestampSchema,
                mandatoryUpdateAfter: UtcTimestampSchema.optional(),
            })
            .strict()
            .optional(),
    })
    .strict()
    .superRefine((state, context) => {
        const validator = state.feedValidator;
        if (
            validator !== undefined &&
            validator.containerSha256 !== state.knownGood?.containerSha256 &&
            validator.containerSha256 !== state.candidate?.containerSha256
        ) {
            context.addIssue({
                code: 'custom',
                path: ['feedValidator'],
                message: 'Feed validator must name the known-good or candidate container.',
            });
        }
    });

export const SambaRuntimeLauncherStateV3Schema: z.ZodType<SambaRuntimeLauncherStateV3> = z
    .object({
        schemaVersion: z.literal(3),
        origins: z.record(
            z.string().refine((value) => {
                try {
                    return new URL(value).origin === value;
                } catch {
                    return false;
                }
            }, 'Origin key must be an origin'),
            OriginStateSchema
        ),
        revocationList: z
            .object({
                sequence: SambaDecimalU63Schema,
                minimumReleaseSequence: SambaDecimalU63Schema,
                revokedArtifactSha256: z.array(SambaSha256HexSchema),
            })
            .strict()
            .optional(),
        lastHttpValidation: z
            .object({
                checkedAt: UtcTimestampSchema,
                result: z.enum(['not-modified', 'current', 'candidate']),
                releaseSequence: SambaDecimalU63Schema,
            })
            .strict()
            .optional(),
        lastFailure: z
            .object({
                occurredAt: UtcTimestampSchema,
                phase: z.enum(['startup', 'update-check']),
                reason: z.enum(['runtime-startup-failed', 'runtime-update-check-failed']),
            })
            .strict()
            .optional(),
    })
    .strict();

export const SambaRuntimeLauncherStateV3StorageContract = {
    variants: { v3: { normalize: SambaRuntimeLauncherStateV3Schema.parse } },
    writerVariant: 'v3',
} as const;

export const SambaRuntimeStateFileName = 'state-v3.json';

// ---------------------------------------------------------------------------
// L0 payload module envelope and rescue context. Frozen.

export const SambaPayloadModuleEnvelopeId = 'anchorbase-payload/1';
export const SambaRescueContextEnvelopeId = 'anchorbase-rescue/1';

export interface SambaRescueContextV1 {
    readonly envelope: typeof SambaRescueContextEnvelopeId;
    readonly launcher: {
        readonly buildId: string;
        readonly nativeVersion: string;
        readonly interface: SambaLauncherInterfaceVersion;
        readonly platform: string;
        readonly architecture: string;
        readonly electronMajor: number;
    };
    readonly portalOrigin: string;
    readonly userDataRoot: string;
    readonly reportStatus: (message: string) => void;
}

export interface SambaPayloadModuleEnvelope {
    readonly envelope: typeof SambaPayloadModuleEnvelopeId;
    readonly launcherInterfaces: readonly SambaLauncherInterfaceRange[];
    readonly start: (major: number, context: unknown) => Promise<unknown>;
    readonly rescue: (context: SambaRescueContextV1) => Promise<void>;
}

// ---------------------------------------------------------------------------
// L2 launcher context, interface major 1.

/** A workflow container the launcher verified for the payload. */
export interface SambaVerifiedWorkflowReleaseV1 {
    readonly statement: SambaWorkflowReleaseStatementV2;
    readonly bundle: Uint8Array;
}

export interface SambaLauncherContextV1 {
    readonly interface: SambaLauncherInterfaceVersion;
    readonly capabilities: readonly string[];
    readonly validationMode: SambaRuntimeValidationMode;
    readonly portalOrigin: string;
    readonly runtimeRoot: string;
    readonly applicationMetadataRoot: string;
    readonly codeCacheRoot: string;
    readonly runtimePayload: SambaRuntimePayloadIdentityV1;
    readonly nativeBuildInfo: CompileTimeMetadata;
    readonly getRuntimeStatus: () => SambaRuntimeStatusV1;
    readonly verifySignedWorkflowRelease: (
        container: Uint8Array,
        expectedModuleName: RpaModuleName
    ) => Promise<SambaVerifiedWorkflowReleaseV1>;
    readonly selectPortalOrigin: (portalOrigin: string) => Promise<void>;
    readonly requestRuntimeReinstall: () => Promise<void>;
    readonly prepareNativeUpdateAdoption: () => Promise<string>;
    readonly runtimeRelaunchCommandTransport?: {
        readonly start: (handleLine: (line: string) => Promise<string | null>) => void;
        readonly waitForParentExit: () => Promise<void>;
        readonly parentApplicationMetadataRoot: string;
        readonly validateRuntimeMetadata: () => Promise<void>;
        readonly activateReplacementProfile: () => SambaRuntimeProfileActivation;
    };
    readonly requestRuntimeRelaunch: (
        captureHandoffCommands: () => Promise<readonly SambaRuntimeRelaunchCommand[]>,
        lifecycle: SambaRuntimeRelaunchLifecycle
    ) => Promise<void>;
    readonly pendingActionLinks: readonly string[];
    readonly markHealthy: () => Promise<void>;
}

/** Capability strings a minor may add. Payloads check before using a member. */
export const SambaLauncherCapabilities = {
    workflowVerification: 'workflow-verification',
    portalSelection: 'portal-selection',
    runtimeReinstall: 'runtime-reinstall',
    nativeUpdateAdoption: 'native-update-adoption',
} as const;

export const SambaNativeUpdateAdoptionArgPrefix = '--native-update-handoff=';
